import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { BooleanInput } from '@angular/cdk/coercion';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';

@Component({
    selector       : 'fuse-vertical-navigation-group-item',
    templateUrl    : './group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FuseVerticalNavigationGroupItemComponent implements OnInit, OnChanges, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_autoCollapse: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() autoCollapse: boolean;
    @Input() item: FuseNavigationItem;
    @Input() name: string;

    isCollapsed: boolean = true;
    isExpanded: boolean = false;
    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _fuseNavigationService: FuseNavigationService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Get the parent navigation component
        this._fuseVerticalNavigationComponent = this._fuseNavigationService.getComponent(this.name);

        // Subscribe to onRefreshed on the navigation component
        this._fuseVerticalNavigationComponent.onRefreshed.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => {

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });

        // Subscribe to navigation end
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        ).subscribe((event: NavigationEnd) => {

            // Check if the current item is expanded
            this._checkIfExpanded(event.urlAfterRedirects);

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });

        // Check if the current item is expanded
        this._checkIfExpanded(this._router.url);
    }

    ngOnChanges(): void
    {
        // Check if the item has changed and update collapsed state
        if (this.item) {
            this._checkIfExpanded(this._router.url);
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle collapsable
     */
    toggleCollapsable(): void
    {
        // Toggle the collapsed status
        this.isCollapsed = !this.isCollapsed;

        // Update the expanded status
        this.isExpanded = !this.isCollapsed;

        // Update the item's collapsed property if it exists
        if (this.item && 'collapsed' in this.item) {
            this.item.collapsed = this.isCollapsed;
        }

        // Force change detection
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Check if the item is expanded
     *
     * @param url
     * @private
     */
    private _checkIfExpanded(url: string): void
    {
        // Check if the navigation item has a collapsed property set
        if (this.item && 'collapsed' in this.item && this.item.collapsed !== undefined) {
            this.isCollapsed = this.item.collapsed;
            this.isExpanded = !this.isCollapsed;
        } else {
            // Check if the current item is expanded based on URL
            this.isExpanded = this._isUrlInChildren(this.item, url);
            this.isCollapsed = !this.isExpanded;
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Check if the URL is in the children
     *
     * @param item
     * @param url
     * @private
     */
    private _isUrlInChildren(item: FuseNavigationItem, url: string): boolean
    {
        if (item.children)
        {
            for (const child of item.children)
            {
                if (child.link && url.includes(child.link))
                {
                    return true;
                }

                if (child.children)
                {
                    if (this._isUrlInChildren(child, url))
                    {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }
}
